import { AxiosInstance, isAxiosError } from 'axios';
import { MutableRefObject, useCallback } from 'react';

import type {
  GetIzziOrdersParams,
  GetSerialsParams,
  IzziSaleOrder,
  PaginatedIzziSaleOrders,
  PaginatedSerialValidations,
  ValidateSerialRequest,
  ValidateSerialResponse,
} from '@/domain/izzi';
import {
  getOrderNumber,
  mapShippingReceiptError,
  parseContentDispositionFilename,
} from '@/utils/shipping-receipt';

export interface IzziRequest {
  validateSerial: (body: ValidateSerialRequest) => Promise<ValidateSerialResponse>;
  getSerials: (params: GetSerialsParams) => Promise<PaginatedSerialValidations>;
  getOrders: (params: GetIzziOrdersParams) => Promise<PaginatedIzziSaleOrders>;
  downloadShippingReceipt: (order: IzziSaleOrder) => Promise<void>;
}

const IZZI_OPS_PATH = '/api/v3/ops-izzi';
const SHIPPING_RECEIPT_PATH = `${IZZI_OPS_PATH}/orders`;

const parseBlobErrorMessage = async (error: unknown): Promise<string> => {
  if (!isAxiosError(error)) {
    return mapShippingReceiptError('');
  }

  const status = error.response?.status;
  const data = error.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text) as { message?: string };
      return mapShippingReceiptError(json.message ?? '', status);
    } catch {
      return mapShippingReceiptError('', status);
    }
  }

  if (typeof data === 'object' && data !== null && 'message' in data) {
    return mapShippingReceiptError(String(data.message), status);
  }

  return mapShippingReceiptError('', status);
};

export default function useIzziRequest(
  httpInstance: MutableRefObject<AxiosInstance | null>,
): IzziRequest {
  const validateSerial = useCallback(
    async (body: ValidateSerialRequest): Promise<ValidateSerialResponse> => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const { data } = await httpInstance.current.post<ValidateSerialResponse>(
        `${IZZI_OPS_PATH}/validate-serial`,
        body,
      );
      return data;
    },
    [httpInstance],
  );

  const getSerials = useCallback(
    async (params: GetSerialsParams): Promise<PaginatedSerialValidations> => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const { data } = await httpInstance.current.get<PaginatedSerialValidations>(
        `${IZZI_OPS_PATH}/serials`,
        { params },
      );
      return data;
    },
    [httpInstance],
  );

  const getOrders = useCallback(
    async (params: GetIzziOrdersParams): Promise<PaginatedIzziSaleOrders> => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const { data } = await httpInstance.current.get<PaginatedIzziSaleOrders>(
        SHIPPING_RECEIPT_PATH,
        { params },
      );
      return data;
    },
    [httpInstance],
  );

  const downloadShippingReceipt = useCallback(
    async (order: IzziSaleOrder): Promise<void> => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');

      const orderNumber = getOrderNumber(order);
      const fallbackFilename = `AcuseEnvio_Izzi_${orderNumber}.pdf`;

      try {
        const response = await httpInstance.current.get<Blob>(
          `${SHIPPING_RECEIPT_PATH}/${order.id}/shipping-receipt`,
          { responseType: 'blob' },
        );

        const filename = parseContentDispositionFilename(
          response.headers['content-disposition'] as string | undefined,
          fallbackFilename,
        );

        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        throw new Error(await parseBlobErrorMessage(error));
      }
    },
    [httpInstance],
  );

  return { validateSerial, getSerials, getOrders, downloadShippingReceipt };
}
